import { Routes } from '@angular/router';

// Import Containers
import { DefaultLayoutComponent } from './containers';

import { SignInComponent } from './auth/sign-in/sign-in.component';
import { IsNotAuthenticated, IsAuthenticated } from './auth/auth.guard';
import { AccessGuard } from './core/access/access.guard';
import { SignUpComponent } from './auth/sign-up/sign-up.component';
import { AnswerChallengeComponent } from './auth/answer-challenge/answer-challenge.component';
import { SignOutComponent } from './auth/sign-out/sign-out.component';
import { NotFoundComponent } from './auth/not-found/not-found.component';
import { NoAccountComponent } from './auth/no-account/no-account.component';
import { TrialOverComponent } from './auth/trial-over/trial-over.component';
import { NotAllowedComponent } from './auth/not-allowed/not-allowed.component';
import { SubscribeComponent } from './auth/subscribe/subscribe.component';
import { PaymentPendingComponent } from './auth/payment-pending/payment-pending.component';
import { PaymentsComponent } from './auth/payments/payments.component';
import { AuthRedirectComponent } from './auth/auth-redirect/auth-redirect.component';
import { AiWorkspaceSetupComponent } from './shared/account/ai-workspace-setup/ai-workspace-setup.component';
import { provideDefaultLayoutModules } from './providers/layout.providers';

export const APP_ROUTES: Routes = [
  { path: 'sign-in', component: SignInComponent, canActivate: [IsNotAuthenticated] },
  { path: 'sign-up', component: SignUpComponent, canActivate: [IsNotAuthenticated] },
  { path: 'enter-secret-code', component: AnswerChallengeComponent, canActivate: [IsNotAuthenticated] },
  { path: 'sign-out', component: SignOutComponent, canActivate: [IsAuthenticated] },
  { path: 'join-workspace', component: NoAccountComponent, canActivate: [IsAuthenticated] },
  { path: 'trial-over/:id', component: TrialOverComponent, canActivate: [IsAuthenticated] },
  { path: 'not-authorized/:id', component: NotAllowedComponent, canActivate: [IsAuthenticated] },
  { path: 'payment-pending/:id', component: PaymentPendingComponent, canActivate: [IsAuthenticated] },
  { path: 'payment/:id', component: PaymentsComponent, canActivate: [IsAuthenticated] },
  { path: 'setup-workspace', component: AiWorkspaceSetupComponent, canActivate: [IsAuthenticated] },
  {
    // Outside DefaultLayout so only buyer-facing content is captured by Meet.
    path: 'salesmax-support/demo-live/buyer',
    canActivate: [IsAuthenticated],
    canActivateChild: [AccessGuard],
    data: { title: 'Buyer presentation', access: 'salesmaxSupport', buyerPresentation: true },
    children: [{ path: '', loadComponent: () => import('./salesmax-support/demo-live/demo-live-buyer.component').then(m => m.DemoLiveBuyerComponent) }],
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivateChild: [AccessGuard],
    providers: [
      provideDefaultLayoutModules(),
    ],
    data: {
      title: 'Home'
    },
    children: [
      {
        path: 'products',
        canActivate: [IsAuthenticated],
        data: { title: 'Products & Inventory', access: 'inventory' },
        loadComponent: () => import('./shared/product/view-products/view-products.component').then(m => m.ViewProductsComponent),
      },
      {
        path: 'inventory/demo',
        canActivate: [IsAuthenticated],
        data: { title: 'Real estate inventory demo', access: 'inventory' },
        loadComponent: () => import('./inventory/inventory-demo.component').then(m => m.InventoryDemoComponent),
      },
      {
        path: 'inventory',
        redirectTo: 'products',
        pathMatch: 'full',
      },
      {
        path: 'oauth/:id',
        component: AuthRedirectComponent,
        canActivate: [IsAuthenticated],
        data: {
          title: 'OAuth'
        }
      },
      {
        path: 'billing',
        canActivate: [IsAuthenticated],
        data: { title: 'Billing', access: 'walletBillingPreview' },
        loadComponent: () => import('./shared/billing/billing-module.component').then(m => m.BillingModuleComponent),
      },
      {
        path: 'billing/:tab',
        canActivate: [IsAuthenticated],
        data: { title: 'Billing', access: 'walletBillingPreview' },
        loadComponent: () => import('./shared/billing/billing-module.component').then(m => m.BillingModuleComponent),
      },
      {
        path: 'wallet',
        canActivate: [IsAuthenticated],
        data: { title: 'Wallet', access: 'wallet' },
        loadChildren: () => import('./wallet/wallet.routes').then(m => m.WALLET_ROUTES),
      },
      {
        path: 'work',
        canActivate: [IsAuthenticated],
        data: { title: 'Work', access: 'workCockpit' },
        loadChildren: () => import('./work/work.routes').then(m => m.WORK_ROUTES),
      },
      { path: 'subscribe', component: SubscribeComponent, canActivate: [IsAuthenticated], data: {
        title: 'Subscribe'
      } },
      {
        path: 'home',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES)
      },
      {
        path: 'engagements',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./engagement/engagement.routes').then(m => m.ENGAGEMENT_ROUTES)
      },
      {
        path: 'activities',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./activities/activities.routes').then(m => m.ACTIVITIES_ROUTES)
      },
      {
        path: 'admin',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES)
      },
      {
        path: 'location',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./location/location.routes').then(m => m.LOCATION_ROUTES)
      },
      {
        path: 'account',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./account/account.routes').then(m => m.ACCOUNT_ROUTES)
      },
      {
        path: 'todo',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./to-do/to-do.routes').then(m => m.TODO_ROUTES)
      },
      {
        path: 'metrics',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./metrics/metrics.routes').then(m => m.METRICS_ROUTES)
      },
      {
        path: 'opportunities',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./opportunity/opportunity.routes').then(m => m.OPPORTUNITY_ROUTES)
      },
      {
        path: 'leads',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./contacts/contacts.routes').then(m => m.CONTACTS_ROUTES)
      },
      {
        path: 'conversations',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./conversations/conversations.routes').then(m => m.CONVERSATIONS_ROUTES)
      },
      {
        path: 'export',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./exports/exports.routes').then(m => m.EXPORTS_ROUTES)
      },
      {
        path: 'salesmax-support',
        canActivate: [ IsAuthenticated ],
        loadChildren: () => import('./salesmax-support/salesmax-support.routes').then(m => m.SALESMAX_SUPPORT_ROUTES)
      }
    ]
  },
  { path: '**', component: NotFoundComponent }
];
